import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import update from 'immutability-helper';

import Appearance from 'styles/Appearance';
import DatePickerField from 'views/DatePickerField.js';
import DateDurationPickerField from 'views/DateDurationPickerField.js';
import Layer from 'structure/Layer.js';
import ScrollMenu from 'react-horizontal-scrolling-menu';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import { VelocityComponent } from 'velocity-react';

const DatePicker = ({ id, blockStyle, date, dateTime, filterDate, filterDateValue, highlightDates, onClose, onDateChange, onRemoveDate, removeable, time, utils }) => {

    const [visible, setVisible] = useState(false);
    const [scale, setScale] = useState(0);
    const [opacity, setOpacity] = useState(0);
    const [dimOpacity, setDimOpacity] = useState(0);
    const [alertButtons, setAlertButtons] = useState([]);
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    })

    const scrollMenu = useRef(null);
    const weeks = [ 0, 1, 2, 3, 4 ];
    const headers = [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun' ];
    const [offset, setOffset] = useState(0);
    const [days, setDays] = useState([]);
    const [target, setTarget] = useState(date ? moment(date) : moment());
    const [selectedDate, setSelectedDate] = useState(date ? moment(date) : moment());
    const [timeOfDay, setTimeOfDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [times, setTimes] = useState([]);

    const onConfirmDate = key => {

        if(key === 'remove') {
            if(typeof(onRemoveDate) === 'function') {
                onRemoveDate(null);
            }
            hide();
            return;
        }

        let dateTarget = moment(selectedDate);
        if(time || dateTime) {
            if(!selectedTime) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'Please select a time before confirming your date'
                });
                return;
            }
            dateTarget = time ? moment(selectedTime) : moment(`${moment(selectedDate).format('YYYY-MM-DD')} ${selectedTime.format('HH:mm:ss')}`)
        }
        hide(() => {
            if(typeof(onDateChange) === 'function') {
                onDateChange(dateTarget);
            }
        });
    }

    const hide = (callback) => {
        setScale(0);
        setOpacity(0);
        setDimOpacity(0);
        setTimeout(() => {
            setVisible(false);
            if(typeof(onClose) === 'function') {
                onClose();
            }
            if(typeof(callback) === 'function') {
                callback();
            }
        }, 250);
    }

    const onWindowSizeChange = (e) => {
        setSize({
            width: window.innerWidth,
            height: window.innerHeight
        })
    }

    const isDateAvailable = newDate => {
        return newDate.isSame(moment(target).add(offset, 'months'), 'month');
    }

    const isTimeAvailable = date => {
        if(typeof(filterDate) === 'function') {
            return filterDate(date);
        }
        return true;
    }

    const shouldHighlightDate = date => {
        if(!highlightDates) {
            return false;
        }
        if(!blockStyle) {
            return highlightDates.find(d => moment(d).unix() === moment(date).unix())
        }
        if(blockStyle === 'week') {
            return moment(date).unix() >= moment(selectedDate).startOf(blockStyle).add(1, 'days').unix() && moment(date).unix() <= moment(selectedDate).endOf(blockStyle).add(1, 'days').unix();
        }
        return moment(date).unix() >= moment(selectedDate).startOf(blockStyle).unix() && moment(date).unix() <= moment(selectedDate).endOf(blockStyle).unix();
    }

    const getMonth = () => {
        return moment(target).startOf('month').add(offset, 'months').format('MMMM YYYY')
    }

    const getTimes = () => {
        return times.filter(time => {
            return time.format('a').toLowerCase() === timeOfDay;
        }).map((time, index) => {

            return (
                <div key={time.unix().toString()}>
                    <span
                    className={'text-button'}
                    style={{
                        ...Appearance.textStyles.title(),
                        fontWeight: '600',
                        textAlign: 'center',
                        fontSize: 14,
                        paddingLeft: 6,
                        paddingRight: 6,
                        ...(selectedTime ? {
                            opacity: isTimeAvailable(time) ? 1 : 0.25,
                            color: isTimeAvailable(time) && time.unix() === selectedTime.unix() ? Appearance.colors.primary() : Appearance.colors.text()
                        } : null)
                    }}>
                        {time.format('h:mm')}
                    </span>
                </div>
            )
        })
    }

    const getDateStyles = (i, length, date) => {

        let highlightDate = shouldHighlightDate(date);
        if(highlightDate && blockStyle) {
            return {
                opacity: 1,
                backgroundColor: Appearance.colors.primary(),
                borderTopLeftRadius: i === 0 ? 10 : 0,
                borderBottomLeftRadius: i === 0 ? 10 : 0,
                borderTopRightRadius: i === length - 1 ? 10 : 0,
                borderBottomRightRadius: i === length - 1 ? 10 : 0
            };
        }
        let available = isDateAvailable(date);
        return {
            opacity: available ? 1 : 0.25
        }
    }

    const getDateTextStyles = date => {

        let highlightDate = shouldHighlightDate(date);
        if(highlightDate && blockStyle) {
            return {
                ...Appearance.textStyles.title(),
                fontWeight: '500',
                textAlign: 'center',
                width: '100%',
                fontSize: 14,
                color: 'white'
            }
        }
        return {
            ...Appearance.textStyles.title(),
            fontWeight: highlightDate ? '600' : '500',
            textAlign: 'center',
            width: '100%',
            fontSize: 14,
            color: selectedDate && date.isSame(moment(selectedDate), 'day') ? 'white' : (highlightDate ? Appearance.colors.primary() : Appearance.textStyles.title().color)
        }
    }

    useEffect(() => {

        if(!target) {
            return;
        }

        // Dates
        // 42 days accounts for 6 full rows of 7 day weeks if first of the month is thursday or later
        let startOfMonth = parseInt(moment(target).startOf('month').add(offset, 'months').subtract(1, 'days').format('e'));
        let totalDays = startOfMonth >= 5 ? 42 : 35;

        let days = [...new Array(totalDays)].map((_, day) => {
            if(day < startOfMonth) {
                return moment(target).startOf('month').add(offset, 'months').subtract(startOfMonth - day, 'days');
            }
            return moment(target).startOf('month').add(offset, 'months').add(day - startOfMonth, 'days');
        });

        let weeks = days.reduce((array, date, index) => {
            let i = Math.floor(index / headers.length);
            if(!array[i]) {
                array[i] = [];
            }
            array[i].push({ date: date });
            return array;
        }, []);
        setDays(weeks);

        // Times
        if(!time && !dateTime) {
            return;
        }

        let index = 0;
        let times = [];
        while (moment(target).add(offset, 'months').startOf('day').add(index * 15, 'minutes') <= moment(target).add(offset, 'months').endOf('day')) {
            let time = moment(target).add(offset, 'months').startOf('day').add(index * 15, 'minutes');
            times.push(time);
            index++;
        }

        let needle = moment(target).unix();
        let closestTime = times.reduce((a, b, index) => {

            // filter out not matching times if applicable
            if(typeof(filterDate) === 'function' && !filterDate(b)) {
                return a;
            }
            // compare unix timestamps
            return Math.abs(b.unix() - needle) < Math.abs(a.unix() - needle) ? b : a;
        }, times[0]);

        let selectedTime = times.find(time => time === closestTime);

        setTimes(times);
        setSelectedTime(selectedTime);
        setTimeOfDay(selectedTime ? moment(selectedTime).format('a').toLowerCase() : moment().format('a').toLowerCase());

    }, [target, offset]);

    useEffect(() => {

        setAlertButtons([{
            key: 'done',
            title: removeable ? 'Use Date' : 'Done',
            style: 'default'
        },{
            key: 'remove',
            title: 'Remove Date',
            style: 'destructive',
            visible: removeable ? true : false
        }]);

        setVisible(true);
        setTimeout(() => {
            setScale(1);
            setOpacity(1);
            setDimOpacity(1);
        }, 100)

        window.addEventListener('resize', onWindowSizeChange);
        return () => {
            window.removeEventListener('resize', onWindowSizeChange);
        }
    }, []);

    return visible ? (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: Appearance.colors.transparent,
            zIndex: 9950
        }}>
            <VelocityComponent
            duration={250}
            animation={{
                opacity: dimOpacity
            }}>
                <div style={{
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    position: 'absolute',
                    backgroundColor: Appearance.colors.dim
                }} />
            </VelocityComponent>

            <VelocityComponent
                easing={[250, 20]}
                duration={500}
                animation={{
                    opacity: opacity,
                    scale: scale
                }}>
                <div style={{
                    borderRadius: 10,
                    backgroundColor: Appearance.colors.alert(),
                    overflow:'hidden',
                    maxHeight: size.height - 30,
                    width: size.width > 400 ? 400 : (size.width - 30),
                    alignItems: 'center',
                    textAlign: 'center',
                    zIndex: 9999
                }}>

                    <div style={{
                        display: 'block',
                        padding: 15
                    }}>
                        {time !== true
                            ?
                            <div style={{
                                padding: 15,
                                borderRadius: 10,
                                border: `1px solid ${Appearance.colors.divider()}`,
                                minHeight: 310
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    paddingLeft: 8,
                                    paddingRight: 8
                                }}>
                                    <img
                                    className={'text-button'}
                                    onClick={() => setOffset(offset => offset - 12)}
                                    src={'images/back-double-arrow-light-grey-small.png'}
                                    style={{
                                        width: 15,
                                        height: 15,
                                        minWidth: 15,
                                        minHeight: 15,
                                        objectFit: 'contain',
                                        marginRight: 4,
                                    }} />

                                    <img
                                    className={'text-button'}
                                    onClick={() => setOffset(offset => offset - 1)}
                                    src={'images/back-arrow-light-grey-small.png'}
                                    style={{
                                        width: 15,
                                        height: 15,
                                        minWidth: 15,
                                        minHeight: 15,
                                        objectFit: 'contain',
                                        marginRight: 8,
                                    }} />

                                    <span style={{
                                        ...Appearance.textStyles.title(),
                                        fontWeight: '600',
                                        textAlign: 'center',
                                        width: '100%',
                                        fontSize: 16,
                                    }}>{getMonth()}</span>

                                    <img
                                    className={'text-button'}
                                    onClick={() => setOffset(offset => offset + 1)}
                                    src={'images/next-arrow-light-grey-small.png'}
                                    style={{
                                        width: 15,
                                        height: 15,
                                        minWidth: 15,
                                        minHeight: 15,
                                        objectFit: 'contain',
                                        marginLeft: 8,
                                    }} />

                                    <img
                                    className={'text-button'}
                                    onClick={() => setOffset(offset => offset + 12)}
                                    src={'images/next-double-arrow-light-grey-small.png'}
                                    style={{
                                        width: 15,
                                        height: 15,
                                        minWidth: 15,
                                        minHeight: 15,
                                        objectFit: 'contain',
                                        marginLeft: 4,
                                    }} />

                                </div>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                    justifyContent: 'space-around',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    height: 40,
                                    marginBottom: 8
                                }}>
                                    {/* header days */}
                                    {headers.map((d, i) => (
                                        <span key={i}
                                        style={{
                                            ...Appearance.textStyles.supporting(),
                                            textAlign: 'center',
                                            fontWeight: '600',
                                            color: Appearance.colors.lightGrey,
                                            flexGrow: 1,
                                            width: '100%'
                                        }}>{d.toUpperCase()}</span>
                                    ))}
                                </div>
                                {days.map((week, index) => {
                                    return (
                                        <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            width: '100%',
                                            justifyContent: 'space-around',
                                            alignItems: 'center',
                                            marginBottom: index !== days.length - 1 ? 4 : 0,
                                        }}>
                                            {week.map((entry, i) => {
                                                return (
                                                    <div
                                                    key={i}
                                                    className={Utils.isMobile() || !entry.date.isSame(target, 'month') ? '' : 'text-button'}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        flexGrow: 1,
                                                        justifyContent: 'center',
                                                        height: '100%',
                                                        width: '100%',
                                                        paddingLeft: 5,
                                                        paddingRight: 5,
                                                        ...getDateStyles(i, week.length, entry.date)
                                                    }}>
                                                        <div onClick={entry.date.isSame(moment(target).add(offset, 'months'), 'month') ? () => {
                                                            setSelectedDate(entry.date.format('YYYY-MM-DD'));
                                                        } : null}
                                                        style={{
                                                            display: 'flex',
                                                            flexGrow: 1,
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            height: '100%',
                                                            width: '100%'
                                                        }}>
                                                            <div
                                                            className={'cursor-pointer'}
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                borderRadius: 8,
                                                                width: 30,
                                                                height: 30,
                                                                padding: 4,
                                                                backgroundColor: selectedDate && entry.date.isSame(moment(selectedDate), 'day') ? Appearance.colors.primary() : null
                                                            }}>
                                                                <span style={getDateTextStyles(entry.date)}>{entry.date.format('D')}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )
                                })}
                            </div>
                            :
                            null
                        }

                        {(time || dateTime) && times.length > 0
                            ?
                            <>
                            <div style={{
                                marginTop: time ? 0 : 15,
                                marginBottom: 4,
                                paddingLeft: 12,
                                paddingRight: 12,
                            }}>
                            <ScrollMenu
                            ref={scrollMenu}
                            data={getTimes()}
                            selected={selectedTime.unix().toString()}
                            scrollToSelected={true}
                            onSelect={unix => {
                                if(!isTimeAvailable(moment.unix(unix))) {
                                    return;
                                }
                                setSelectedTime(moment.unix(unix));
                            }}
                            arrowClass={'text-button'}
                            arrowLeft={(
                                <img
                                src={'images/back-arrow-light-grey-small.png'}
                                style={{
                                    width: 15,
                                    height: 15,
                                    minWidth: 15,
                                    minHeight: 15,
                                    marginRight: 8,
                                    objectFit: 'contain'
                                }} />
                            )}
                            arrowRight={(
                                <img
                                src={'images/next-arrow-light-grey-small.png'}
                                style={{
                                    width: 15,
                                    height: 15,
                                    minWidth: 15,
                                    minHeight: 15,
                                    marginLeft: 8,
                                    objectFit: 'contain'
                                }} />
                            )} />
                            </div>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: '100%',
                                justifyContent: 'center'
                            }}>
                                <span
                                className={'text-button'}
                                onClick={() => setTimeOfDay('am')}
                                style={{
                                    ...Appearance.textStyles.title(),
                                    fontWeight: '700',
                                    textAlign: 'center',
                                    fontSize: 16,
                                    marginLeft: 8,
                                    marginRight: 8,
                                    color: timeOfDay === 'am' ? Appearance.colors.primary() : Appearance.colors.text()
                                }}>
                                    {'AM'}
                                </span>
                                <span
                                className={'text-button'}
                                onClick={() => setTimeOfDay('pm')}
                                style={{
                                    ...Appearance.textStyles.title(),
                                    fontWeight: '700',
                                    textAlign: 'center',
                                    fontSize: 16,
                                    marginLeft: 8,
                                    marginRight: 8,
                                    color: timeOfDay === 'pm' ? Appearance.colors.primary() : Appearance.colors.text()
                                }}>
                                    {'PM'}
                                </span>
                            </div>
                            </>
                            :
                            null
                        }
                    </div>

                    {alertButtons.map(item => {

                        if(item.visible === false) {
                            return null;
                        }
                        return (
                            <div
                            key={item.key}
                            className={`alert-item ${window.theme}`}
                            onClick={onConfirmDate.bind(this, item.key)}
                            style={{
                                height: 50,
                                width: '100%'
                            }}>
                                <div style={{
                                    height: 50
                                }}>
                                    <div style={{
                                        width: '100%',
                                        height: 1,
                                        backgroundColor: Appearance.colors.divider()
                                    }}/>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 50
                                    }}>
                                        <span style={{
                                            fontSize: '13px',
                                            fontWeight: '400',
                                            textAlign:'center',
                                            color: (item.style == 'cancel' || item.style == 'destructive') ? Appearance.colors.text() : Appearance.colors.primary()
                                        }}>{item.title}</span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </VelocityComponent>
        </div>
    ) : null
}

export default DatePicker;

export const DualDatePicker = (props, { options, utils }) => {

    const layerID = 'dual_date_picker_alert';
    const [loading, setLoading] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [startDate, setStartDate] = useState(props.startDate);
    const [endDate, setEndDate] = useState(props.endDate);

    const onStartDateChange = date => {
        setStartDate(date);
    }

    const onEndDateChange = date => {
        setEndDate(date);
    }

    const onConfirmDates = () => {
        setLayerState('close');
        if(typeof(props.onDateChange) === 'function') {
            props.onDateChange({
                start: startDate,
                end: endDate
            })
        }
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            onClick: onConfirmDates,
            text: 'Done'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        title={props.title || 'Date Picker'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: props.showTime === false ? 'small' : 'medium'
        }}>
            <span
            className={'d-block mb-1'}
            style={Appearance.textStyles.title()}>{'Start Date'}</span>
            {props.showTime === false
                ?
                <DatePickerField
                {...props}
                utils={utils}
                selected={startDate}
                onDateChange={onStartDateChange} />
                :
                <DateDurationPickerField
                {...props}
                onChange={onStartDateChange}
                utils={utils}
                value={startDate} />
            }

            <span
            className={'d-block mt-3 mb-1'}
            style={Appearance.textStyles.title()}>{'End Date'}</span>
            {props.showTime === false
                ?
                <DatePickerField
                {...props}
                utils={utils}
                selected={endDate}
                onDateChange={onEndDateChange} />
                :
                <DateDurationPickerField
                {...props}
                onChange={onEndDateChange}
                utils={utils}
                value={endDate} />
            }

        </Layer>
    )
}
